/* Arbre baobab SVG : tronc = Bombacare, branches = centres (parrainage → sous-branches), feuilles = patients actifs, sève animée. */
import { useMemo } from "react";

export const BaobabMotif = ({ className = "bb-motif" }) => (
  <svg viewBox="0 0 320 300" className={className} aria-hidden="true">
    <path d="M150 300 L150 170 C150 120 120 100 60 70 M150 170 C150 110 190 90 250 50 M150 200 C110 170 80 160 30 150 M150 190 C190 160 230 150 290 140 M150 150 C140 110 130 80 110 30 M150 140 C160 100 180 70 200 20"
      fill="none" stroke="#fff" strokeWidth="14" strokeLinecap="round" />
  </svg>
);

function layout(centres, W, H) {
  const roots = centres.filter((c) => !c.parrain_id);
  const kidsOf = (id) => centres.filter((c) => c.parrain_id === id);
  const trunkTop = { x: W / 2, y: H * 0.60 };
  const nodes = [];
  const n = Math.max(1, roots.length);
  roots.forEach((c, i) => {
    const t = n === 1 ? 0.38 : i / (n - 1);
    const x = W * 0.16 + t * W * 0.68;
    const y = H * 0.34 - Math.abs(t - 0.5) * H * 0.12;
    nodes.push({ ...c, x, y, from: trunkTop, depth: 1 });
    const kids = kidsOf(c.id);
    kids.forEach((k, j) => {
      const dir = x <= W / 2 ? 1 : -1;
      const kx = x + dir * (0.26 + j * 0.12) * W;
      const ky = y - H * 0.06 + (j % 2) * H * 0.14;
      nodes.push({ ...k, x: Math.max(W * 0.1, Math.min(W * 0.9, kx)), y: Math.max(H * 0.14, ky), from: { x, y }, depth: 2 });
    });
  });
  return { trunkTop, nodes };
}

const curve = (a, b) => `M${a.x},${a.y} C${a.x},${(a.y + b.y) / 2} ${b.x},${(a.y + b.y) / 2} ${b.x},${b.y}`;

export function BaobabTree({ data, selected, onSelect, compact = false }) {
  const W = 900, H = compact ? 420 : 520;
  const { trunkTop, nodes } = useMemo(() => layout(data.centres, W, H), [data, H]);
  const base = { x: W / 2, y: H - 70 };
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto select-none" data-testid="baobab-tree" role="img" aria-label="Réseau Bombacare en forme de baobab">
      <defs>
        <linearGradient id="bbTrunk" x1="0" y1="1" x2="0" y2="0"><stop offset="0" stopColor="#0A1426" /><stop offset="1" stopColor="#0A1426" /></linearGradient>
        <radialGradient id="bbGlow"><stop offset="0" stopColor="#D4AF5A" stopOpacity=".55" /><stop offset="1" stopColor="#D4AF5A" stopOpacity="0" /></radialGradient>
      </defs>
      {/* racines */}
      {[-1, -0.5, 0.5, 1].map((d) => <path key={d} d={`M${base.x},${base.y} C${base.x + d * 40},${base.y + 10} ${base.x + d * 90},${base.y + 20} ${base.x + d * 150},${base.y + 36}`} fill="none" stroke="#0A1426" strokeWidth="10" strokeLinecap="round" opacity=".5" />)}
      {/* tronc */}
      <path d={`M${base.x - 64},${base.y} C${base.x - 70},${base.y - 70} ${base.x - 34},${trunkTop.y + 50} ${base.x - 22},${trunkTop.y} L${base.x + 22},${trunkTop.y} C${base.x + 34},${trunkTop.y + 50} ${base.x + 70},${base.y - 70} ${base.x + 64},${base.y} Z`} fill="url(#bbTrunk)" />
      <path d={`M${base.x - 30},${base.y - 10} C${base.x - 28},${base.y - 90} ${base.x - 14},${trunkTop.y + 60} ${base.x - 8},${trunkTop.y + 10}`} fill="none" stroke="#fff" strokeOpacity=".08" strokeWidth="6" strokeLinecap="round" />
      <line x1={base.x} y1={base.y} x2={trunkTop.x} y2={trunkTop.y} className="bb-sap slow" strokeWidth="3" fill="none" />
      <g data-testid="baobab-root" onClick={() => onSelect?.(null)} className="cursor-pointer">
        <circle cx={base.x} cy={base.y - 2} r="30" fill="url(#bbGlow)" />
        <text x={base.x} y={base.y + 60} textAnchor="middle" fontFamily="Fraunces, serif" fontWeight="700" fontSize="20" fill="#0A1426">{data.racine.nom}</text>
        <text x={base.x} y={base.y + 80} textAnchor="middle" fontFamily="Manrope, sans-serif" fontSize="11" fill="#0A1426" opacity=".75">{data.racine.centres} centres · {data.racine.patients} patients · {data.racine.fruits ?? 0} fruits</text>
      </g>
      {/* branches */}
      {nodes.map((c) => {
        const sel = selected === c.id;
        const leaves = Math.min(14, c.patients_actifs || 0);
        const ghost = Math.min(14, Math.max(0, (c.patients || 0) - leaves));
        const all = leaves + ghost;
        return (
          <g key={c.id} data-testid={`baobab-branch-${c.id}`} onClick={() => onSelect?.(c.id)} className="cursor-pointer" style={{ transition: "opacity .3s", opacity: selected && !sel && selected !== c.parrain_id ? 0.45 : 1 }}>
            <path d={curve(c.from, c)} fill="none" stroke={sel ? "#D4AF5A" : c.est_moi ? "#2BA3A0" : "#0A1426"} strokeWidth={c.depth === 1 ? 16 : 9} strokeLinecap="round" opacity=".95" />
            <path d={curve(c.from, c)} fill="none" className="bb-sap" strokeWidth={c.depth === 1 ? 2.5 : 2} />
            {Array.from({ length: all }).map((_, i) => {
              const ang = Math.PI + (i / Math.max(1, all - 1)) * Math.PI;
              const r = 44 + (i % 3) * 14;
              const lx = c.x + Math.cos(ang) * r, ly = c.y + Math.sin(ang) * r * 0.8;
              return <circle key={i} cx={lx} cy={ly} r={i < leaves ? 6 : 4.5} fill={i < leaves ? "#22c55e" : "#D8D3C4"} className={i < leaves ? "bb-leaf" : ""} style={{ animationDelay: `${(i % 5) * 0.4}s` }} />;
            })}
            {Array.from({ length: Math.min(9, c.fruits?.total || 0) }).map((_, i) => {
              const ang = Math.PI * 1.15 + (i / 9) * Math.PI * 0.7;
              const r = 72 + (i % 2) * 10;
              const fx = c.x + Math.cos(ang) * r, fy = c.y + Math.sin(ang) * r * 0.85;
              return <g key={`f${i}`} data-testid="baobab-fruit" className="bb-leaf" style={{ animationDelay: `${i * 0.3}s` }}><circle cx={fx} cy={fy} r="6.5" fill="#D4AF5A" stroke="#fff" strokeWidth="1.5" /><circle cx={fx - 2} cy={fy - 2} r="1.8" fill="#fff" opacity=".7" /></g>;
            })}
            <circle cx={c.x} cy={c.y} r={sel ? 20 : 15} fill={sel ? "#D4AF5A" : c.est_moi ? "#2BA3A0" : "#0A1426"} stroke="#fff" strokeWidth="3" style={{ transition: "r .25s" }} />
            {c.est_moi && <circle cx={c.x} cy={c.y} r="27" fill="none" stroke="#2BA3A0" strokeWidth="2" strokeDasharray="4 6" opacity=".8" />}
            <text x={c.x} y={c.y + 40} textAnchor="middle" fontFamily="Fraunces, serif" fontWeight="700" fontSize={compact ? 13 : 15} fill="#0A1426">{c.nom.replace("Centre Bombacare ", "")}</text>
            <text x={c.x} y={c.y + 56} textAnchor="middle" fontFamily="Manrope, sans-serif" fontSize="11" fill="#0A1426" opacity=".75">{c.praticien} · {c.patients} patient{c.patients > 1 ? "s" : ""}</text>
          </g>
        );
      })}
    </svg>
  );
}
